// ISDP CUSTOM: Per-language TechDocs bridge.
//
// Teams author per-language docs with mkdocs-static-i18n (suffix mode): an
// `index.de.md` next to `index.md`. The plugin builds English at the site root
// and each other language under a locale prefix, e.g. German at `/de/…`. Pages
// with no German sibling fall back to English (the plugin copies the default
// language into the localised sub-tree), so the /de/ sub-tree is always
// complete.
//
// Backstage has no notion of this locale sub-tree — the reader just serves
// whatever path is in the URL. This addon bridges the app's language selection
// (appLanguageApiRef) to the TechDocs reader:
//
//   * When the app language is `de` and the current reader path is NOT already
//     under `/de/`, we HEAD-probe the German build path. If it exists we
//     redirect the reader into the `/de/…` sub-tree, preserving the current
//     sub-page. If the probe 404s (no German build for this entity) we stay on
//     English.
//   * When the language flips back to `en`, we strip the `/de/` prefix and
//     navigate back to the English page.
//
// It re-runs on language change and on reader navigation (pathname change).
//
// The reader route is `/docs/:namespace/:kind/:name/*`; the `*` is the doc
// sub-path (mirrors the mkdocs build path). The matching TechDocs static asset
// lives at `<apiOrigin>/static/docs/<ns>/<kind>/<name>/<subpath>` — we probe the
// German variant of that.
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPlugin, useApi } from '@backstage/core-plugin-api';
import { appLanguageApiRef } from '@backstage/core-plugin-api/alpha';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
  techdocsStorageApiRef,
} from '@backstage/plugin-techdocs-react';

const LOCALE = 'de';

type ParsedDocPath = {
  ns: string;
  kind: string;
  name: string;
  // Doc sub-path within the entity, no leading slash. May be '' for the root.
  sub: string;
  // True when `sub` already starts with the German locale segment.
  isGerman: boolean;
};

// Parse `/docs/:ns/:kind/:name/<sub...>` out of a pathname. Returns null for
// anything that is not a reader route. Exported for unit testing.
export const parseDocPath = (pathname: string): ParsedDocPath | null => {
  // Strip leading and trailing slashes so a trailing '/' does not create an
  // empty final segment (which would leak into `sub`).
  const parts = pathname.replace(/^\/+/, '').replace(/\/+$/, '').split('/');
  if (parts[0] !== 'docs' || parts.length < 4) {
    return null;
  }
  const [, ns, kind, name, ...rest] = parts;
  const isGerman = rest[0] === LOCALE;
  const sub = (isGerman ? rest.slice(1) : rest).join('/');
  return { ns, kind, name, sub, isGerman };
};

// Build the app reader path for a doc entity + sub-path (+ optional locale).
// Exported for unit testing.
export const buildReaderPath = (
  p: ParsedDocPath,
  { german }: { german: boolean },
): string => {
  const segs = ['docs', p.ns, p.kind, p.name];
  if (german) {
    segs.push(LOCALE);
  }
  if (p.sub) {
    segs.push(...p.sub.split('/'));
  }
  return `/${segs.join('/')}`;
};

// Build the TechDocs static asset path for the German variant of the current
// sub-page, so we can HEAD-probe whether the /de/ build exists. Exported for
// unit testing.
export const buildGermanStaticPath = (p: ParsedDocPath): string => {
  const sub = p.sub || '';
  // The reader sub-path maps to the mkdocs build path. Directory-style pages
  // (no file extension) resolve to `<sub>/index.html`; a bare entity root
  // resolves to `index.html`.
  let assetSub: string;
  if (!sub) {
    assetSub = 'index.html';
  } else if (/\.[a-z0-9]+$/i.test(sub)) {
    assetSub = sub;
  } else {
    assetSub = `${sub.replace(/\/$/, '')}/index.html`;
  }
  return `static/docs/${p.ns}/${p.kind}/${p.name}/${LOCALE}/${assetSub}`;
};

const TechDocsLanguageProxyAddon = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const languageApi = useApi(appLanguageApiRef);
  const storageApi = useApi(techdocsStorageApiRef);

  // Current app language, kept in a ref so the navigation effect can read it
  // without re-subscribing.
  const languageRef = useRef<string>(languageApi.getLanguage().language);

  // Avoid probing/redirecting the same target twice in a row.
  const lastHandledRef = useRef<string>('');

  useEffect(() => {
    let cancelled = false;

    const reconcile = async () => {
      const parsed = parseDocPath(window.location.pathname);
      if (!parsed) {
        return;
      }
      const language = languageRef.current;

      // English selected but we're under /de/ — go back to English.
      if (language !== LOCALE && parsed.isGerman) {
        navigate(buildReaderPath(parsed, { german: false }), { replace: true });
        return;
      }

      // German selected and not already under /de/ — probe, then redirect.
      if (language === LOCALE && !parsed.isGerman) {
        const probeKey = `${language}:${window.location.pathname}`;
        if (lastHandledRef.current === probeKey) {
          return;
        }
        lastHandledRef.current = probeKey;
        try {
          const origin = await storageApi.getApiOrigin();
          const url = `${origin.replace(/\/$/, '')}/${buildGermanStaticPath(
            parsed,
          )}`;
          const res = await fetch(url, {
            method: 'HEAD',
            credentials: 'include',
          });
          if (cancelled) {
            return;
          }
          if (res.ok) {
            navigate(buildReaderPath(parsed, { german: true }), {
              replace: true,
            });
          }
          // 404 → German build missing for this page; stay on English.
        } catch {
          // Network error → stay on English (graceful fallback).
        }
      }
    };

    void reconcile();
    return () => {
      cancelled = true;
    };
    // Re-run on navigation (pathname change).
  }, [location.pathname, navigate, storageApi]);

  // Subscribe to language changes and re-run reconciliation.
  useEffect(() => {
    const subscription = languageApi.language$().subscribe(({ language }) => {
      languageRef.current = language;
      // Clear the de-dupe guard so a fresh probe runs for the new language.
      lastHandledRef.current = '';
      const parsed = parseDocPath(window.location.pathname);
      if (!parsed) {
        return;
      }
      if (language !== LOCALE && parsed.isGerman) {
        navigate(buildReaderPath(parsed, { german: false }), { replace: true });
        return;
      }
      if (language === LOCALE && !parsed.isGerman) {
        void (async () => {
          try {
            const origin = await storageApi.getApiOrigin();
            const url = `${origin.replace(/\/$/, '')}/${buildGermanStaticPath(
              parsed,
            )}`;
            const res = await fetch(url, {
              method: 'HEAD',
              credentials: 'include',
            });
            if (res.ok) {
              navigate(buildReaderPath(parsed, { german: true }), {
                replace: true,
              });
            }
          } catch {
            /* stay on English */
          }
        })();
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [languageApi, navigate, storageApi]);

  return null;
};

const techdocsLanguageProxyPlugin = createPlugin({
  id: 'techdocs-language-proxy',
});

export const TechDocsLanguageProxy = techdocsLanguageProxyPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsLanguageProxy',
    location: TechDocsAddonLocations.Content,
    component: TechDocsLanguageProxyAddon,
  }),
);
